import { produce } from "immer";
import type {
  EditorState,
  Node,
  SerializedNodes,
  EditorActions,
  CraftConfig,
} from "./types";
import type { HistoryManager } from "./history";
import { getDescendants } from "./selectors";
import type { Resolver } from "../resolver/resolver";

function wouldCreateCycle(
  nodes: Record<string, Node>,
  nodeId: string,
  targetParentId: string,
): boolean {
  let currentId: string | null = targetParentId;
  while (currentId) {
    if (currentId === nodeId) return true;
    currentId = nodes[currentId]?.data.parent ?? null;
  }
  return false;
}

function removeChildNode(childIds: string[], nodeId: string): void {
  const index = childIds.indexOf(nodeId);
  if (index >= 0) {
    childIds.splice(index, 1);
  }
}

export function createActions(
  getState: () => EditorState,
  setState: (state: EditorState) => void,
  history: HistoryManager,
  resolver: Resolver,
): EditorActions {
  function setContentState(
    previousState: EditorState,
    nextState: EditorState,
  ): void {
    if (nextState === previousState) return;
    setState({
      ...nextState,
      contentRevision: previousState.contentRevision + 1,
    });
  }

  function mutate(recipe: (draft: EditorState) => void): void {
    const currentState = getState();
    const next = history.add(currentState, recipe);
    setContentState(currentState, next);
  }

  function mutateThrottled(
    recipe: (draft: EditorState) => void,
    throttleRate: number,
  ): void {
    const currentState = getState();
    const next = history.add(currentState, recipe, throttleRate);
    setContentState(currentState, next);
  }

  function mutateNoHistory(recipe: (draft: EditorState) => void): void {
    const currentState = getState();
    const next = produce(currentState, recipe);
    setContentState(currentState, next);
  }

  const actions: EditorActions = {
    add(nodeTree, parentId, index) {
      actions.addNodeTree(nodeTree, parentId, index);
    },

    addNodeTree(nodeTree, parentId, index) {
      mutate((draft) => {
        const parent = draft.nodes[parentId];
        const incomingRoot = nodeTree.nodes[nodeTree.rootNodeId];
        if (!parent || !incomingRoot) return;

        for (const [id, node] of Object.entries(nodeTree.nodes)) {
          draft.nodes[id] = node;
        }

        const rootNode = draft.nodes[nodeTree.rootNodeId];
        if (!rootNode) return;

        rootNode.data.parent = parentId;
        removeChildNode(parent.data.nodes, nodeTree.rootNodeId);

        if (index !== undefined && index >= 0) {
          const clampedIndex = Math.min(index, parent.data.nodes.length);
          parent.data.nodes.splice(clampedIndex, 0, nodeTree.rootNodeId);
        } else {
          parent.data.nodes.push(nodeTree.rootNodeId);
        }
      });
    },

    delete(nodeId) {
      mutate((draft) => {
        const node = draft.nodes[nodeId];
        if (!node || nodeId === "ROOT") return;

        if (node.data.parent) {
          const parent = draft.nodes[node.data.parent];
          if (parent) {
            removeChildNode(parent.data.nodes, nodeId);
            for (const [key, linkedId] of Object.entries(
              parent.data.linkedNodes,
            )) {
              if (linkedId === nodeId) {
                delete parent.data.linkedNodes[key];
              }
            }
          }
        }

        const descendants = getDescendants(
          draft as unknown as EditorState,
          nodeId,
        );
        for (const descId of descendants) {
          delete draft.nodes[descId];
          draft.events.selected.delete(descId);
          draft.events.hovered.delete(descId);
          draft.events.dragged.delete(descId);
        }

        delete draft.nodes[nodeId];
        draft.events.selected.delete(nodeId);
        draft.events.hovered.delete(nodeId);
        draft.events.dragged.delete(nodeId);
      });
    },

    move(nodeId, targetParentId, index) {
      mutate((draft) => {
        if (nodeId === "ROOT" || nodeId === targetParentId) return;

        const node = draft.nodes[nodeId];
        const newParent = draft.nodes[targetParentId];
        if (!node || !newParent) return;
        if (wouldCreateCycle(draft.nodes, nodeId, targetParentId)) return;

        if (node.data.parent) {
          const oldParent = draft.nodes[node.data.parent];
          if (oldParent) {
            removeChildNode(oldParent.data.nodes, nodeId);
            for (const [key, linkedId] of Object.entries(oldParent.data.linkedNodes)) {
              if (linkedId === nodeId) {
                delete oldParent.data.linkedNodes[key];
              }
            }
          }
        }

        node.data.parent = targetParentId;
        removeChildNode(newParent.data.nodes, nodeId);
        const clampedIndex = Math.max(
          0,
          Math.min(index, newParent.data.nodes.length),
        );
        newParent.data.nodes.splice(clampedIndex, 0, nodeId);
      });
    },

    setProp(nodeId, cb) {
      mutate((draft) => {
        const node = draft.nodes[nodeId];
        if (node) {
          cb(node.data.props);
        }
      });
    },

    setCustom(nodeId, cb) {
      mutate((draft) => {
        const node = draft.nodes[nodeId];
        if (node) {
          cb(node.data.custom);
        }
      });
    },

    selectNode(nodeId) {
      // Lightweight manual state update instead of Immer produce
      const state = getState();

      // Skip if already selected (same single selection)
      if (
        nodeId &&
        state.events.selected.size === 1 &&
        state.events.selected.has(nodeId)
      ) {
        return;
      }
      if (!nodeId && state.events.selected.size === 0) {
        return;
      }

      const updatedNodes = { ...state.nodes };

      // Clear previous selection
      for (const prevId of state.events.selected) {
        const prevNode = updatedNodes[prevId];
        if (prevNode) {
          updatedNodes[prevId] = { ...prevNode, events: { ...prevNode.events, selected: false } };
        }
      }

      const newSelected = new Set<string>();
      if (nodeId) {
        newSelected.add(nodeId);
        const node = updatedNodes[nodeId];
        if (node) {
          updatedNodes[nodeId] = { ...node, events: { ...node.events, selected: true } };
        }
      }

      setState({
        ...state,
        nodes: updatedNodes,
        events: { ...state.events, selected: newSelected },
      });
    },

    setHidden(nodeId, hidden) {
      mutate((draft) => {
        const node = draft.nodes[nodeId];
        if (node) {
          node.data.hidden = hidden;
        }
      });
    },

    deserialize(data) {
      const serialized: SerializedNodes =
        typeof data === "string" ? JSON.parse(data) : data;

      mutateNoHistory((draft) => {
        draft.nodes = {} as Record<string, Node>;
        draft.events.selected.clear();
        draft.events.hovered.clear();
        draft.events.dragged.clear();

        for (const [id, serializedNode] of Object.entries(serialized)) {
          const resolvedName =
            typeof serializedNode.type === "object"
              ? serializedNode.type.resolvedName
              : serializedNode.type;

          const component = resolver.resolve(resolvedName as string);
          if (!component) {
            console.warn(
              `Could not resolve component: ${resolvedName}`,
            );
            continue;
          }

          const craft = (
            component as unknown as { craft?: CraftConfig }
          ).craft;
          const displayName =
            serializedNode.displayName ?? (resolvedName as string);

          const node: Node = {
            id,
            dom: null,
            data: {
              type: component,
              isCanvas: serializedNode.isCanvas ?? false,
              props: { ...(craft?.props ?? {}), ...(serializedNode.props ?? {}) },
              displayName,
              name: displayName,
              custom: { ...(craft?.custom ?? {}), ...(serializedNode.custom ?? {}) },
              parent: serializedNode.parent ?? null,
              nodes: serializedNode.nodes ?? [],
              linkedNodes: serializedNode.linkedNodes ?? {},
              hidden: serializedNode.hidden ?? false,
            },
            events: { selected: false, hovered: false, dragged: false },
            related: craft?.related ?? {},
            rules: {
              canDrag: (craft?.rules?.canDrag as Node["rules"]["canDrag"]) ?? (() => true),
              canDrop: (craft?.rules?.canDrop as Node["rules"]["canDrop"]) ?? (() => true),
              canMoveIn: (craft?.rules?.canMoveIn as Node["rules"]["canMoveIn"]) ?? (() => true),
              canMoveOut: (craft?.rules?.canMoveOut as Node["rules"]["canMoveOut"]) ?? (() => true),
            },
          };

          draft.nodes[id] = node;
        }
      });

      history.clear();
    },

    clearEvents() {
      const state = getState();
      const touchedNodeIds = new Set<string>([
        ...state.events.selected,
        ...state.events.hovered,
        ...state.events.dragged,
      ]);
      if (touchedNodeIds.size === 0) return;

      const updatedNodes = { ...state.nodes };
      for (const id of touchedNodeIds) {
        const node = state.nodes[id];
        if (!node) continue;
        if (!node.events.selected && !node.events.hovered && !node.events.dragged) {
          continue;
        }
        updatedNodes[id] = {
          ...node,
          events: { selected: false, hovered: false, dragged: false },
        };
      }

      setState({
        ...state,
        nodes: updatedNodes,
        events: {
          selected: new Set(),
          hovered: new Set(),
          dragged: new Set(),
        },
      });
    },

    history: {
      undo() {
        const currentState = getState();
        const result = history.undo(currentState);
        if (result) setContentState(currentState, result);
      },
      redo() {
        const currentState = getState();
        const result = history.redo(currentState);
        if (result) setContentState(currentState, result);
      },
      canUndo: () => history.canUndo(),
      canRedo: () => history.canRedo(),
      clear: () => history.clear(),
      throttle(rate = 500) {
        return {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setProp(nodeId: string, cb: (props: any) => void) {
            mutateThrottled((draft) => {
              const node = draft.nodes[nodeId];
              if (node) {
                cb(node.data.props);
              }
            }, rate);
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setCustom(nodeId: string, cb: (custom: any) => void) {
            mutateThrottled((draft) => {
              const node = draft.nodes[nodeId];
              if (node) {
                cb(node.data.custom);
              }
            }, rate);
          },
        };
      },
    },
  };

  return actions;
}
